import React from 'react';
import {
  Box,
  Paper,
  Typography,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Badge,
  alpha,
  Divider,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

const CategoryPanel = ({ 
  categories = [], 
  selectedCategory, 
  onSelectCategory,
  totalProducts = 0 
}) => {
  const { t } = useTranslation();

  return (
    <Paper
      elevation={2}
      sx={{
        width: { xs: 280, sm: 280, md: 260 },
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'white',
        borderRight: '1px solid',
        borderColor: 'divider',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 2,
          borderBottom: '2px solid',
          borderColor: 'divider',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.1rem', mb: 0.5 }}>
          {t('categories') || 'Categories'}
        </Typography>
        <Typography variant="caption" sx={{ opacity: 0.9 }}>
          {categories.length} {t('categories_available') || 'categories'}
        </Typography>
      </Box>

      {/* Categories List */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          '&::-webkit-scrollbar': {
            width: '6px',
          },
          '&::-webkit-scrollbar-track': {
            bgcolor: alpha('#000', 0.05),
          },
          '&::-webkit-scrollbar-thumb': {
            bgcolor: alpha('#667eea', 0.3),
            borderRadius: '3px',
            '&:hover': {
              bgcolor: alpha('#667eea', 0.5),
            },
          },
        }}
      >
        {categories.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4, px: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {t('no_categories') || 'No categories available'}
            </Typography>
          </Box>
        ) : (
          <List sx={{ p: 0 }}>
            {categories.map((category, index) => {
              const isSelected = selectedCategory?.id === category.id;
              
              return (
                <React.Fragment key={category.id}>
                  <ListItemButton
                    selected={isSelected}
                    onClick={() => onSelectCategory(category)}
                    sx={{
                      py: 1.5,
                      px: 2,
                      transition: 'all 0.2s ease',
                      borderLeft: '4px solid',
                      borderLeftColor: isSelected ? '#667eea' : 'transparent',
                      bgcolor: isSelected ? alpha('#667eea', 0.08) : 'transparent',
                      '&:hover': {
                        bgcolor: isSelected 
                          ? alpha('#667eea', 0.12)
                          : alpha('#667eea', 0.04),
                        borderLeftColor: '#667eea',
                      },
                      '&.Mui-selected': {
                        bgcolor: alpha('#667eea', 0.08),
                        '&:hover': {
                          bgcolor: alpha('#667eea', 0.12),
                        },
                      },
                    }}
                  >
                    {/* Category Icon */}
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <Box
                        sx={{
                          fontSize: '1.5rem',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 36,
                          height: 36,
                          borderRadius: '8px',
                          bgcolor: isSelected 
                            ? alpha('#667eea', 0.15)
                            : alpha('#667eea', 0.05),
                          transition: 'all 0.2s ease',
                        }}
                      >
                        {category.icon || '📦'}
                      </Box>
                    </ListItemIcon>

                    {/* Category Name */}
                    <ListItemText
                      primary={category.name}
                      primaryTypographyProps={{
                        sx: {
                          fontWeight: isSelected ? 700 : 600,
                          fontSize: '0.9rem',
                          color: isSelected ? '#667eea' : 'text.primary',
                        },
                      }}
                    />

                    {/* Product Count Badge */}
                    <Badge
                      badgeContent={category.count || 0}
                      color="primary"
                      sx={{
                        '& .MuiBadge-badge': {
                          bgcolor: isSelected ? '#667eea' : alpha('#667eea', 0.7),
                          color: 'white',
                          fontWeight: 700,
                          fontSize: '0.7rem',
                          minWidth: 22,
                          height: 22,
                          borderRadius: '11px',
                        },
                      }}
                    />
                  </ListItemButton>
                  
                  {/* Divider between items (except last item) */}
                  {index < categories.length - 1 && (
                    <Divider sx={{ mx: 2 }} />
                  )}
                </React.Fragment>
              );
            })}
          </List>
        )}
      </Box>

      {/* Footer Info */}
      <Box
        sx={{
          p: 2,
          borderTop: '1px solid',
          borderColor: 'divider',
          bgcolor: alpha('#667eea', 0.03),
        }}
      >
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center' }}>
          {selectedCategory?.name && (
            <>
              <strong>{selectedCategory.name}</strong>
              <br />
              {selectedCategory.count || 0} {t('products') || 'products'}
            </>
          )}
        </Typography>
      </Box>
    </Paper>
  );
};

export default CategoryPanel;